import React, { SetStateAction, useState } from 'react';
import CommonConfigDialog from './CommonConfigDialog';
import { DialogInput, RequiredDialogSelect, DialogCheckbox } from '../../DialogInputs';
import { FunctionSelect } from '../../FunctionSelect';
import { handleInputChange } from '../../Utilities/DialogUtils';
import { useLines, useQualities, useLabFunctions, useOffspecFunctions } from '../../Utilities/Hooks';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { LineQualityData } from '../../Types/LineQualityData';
import { Line } from '../../Types/Line';
import { Quality } from '../../Types/Quality';
import { BusinessUnit } from '../../Types/BusinessUnit';

interface LineQualityDataEditDialogProps {
    dialogData: LineQualityData,
    setDialogData: React.Dispatch<SetStateAction<LineQualityData>>,
    showDialog: boolean,
    closeDialog: Function,
    businessUnits: BusinessUnit[]
}

function LineQualityDataEditDialog({ dialogData, setDialogData, showDialog, closeDialog, businessUnits }: LineQualityDataEditDialogProps) {

    const { fetchDataAuthenticated } = useHttpUtils();
    const [fieldErrors, setFieldErrors] = useState<Partial<LineQualityData>>({});
    const lines = useLines(false, dialogData.businessUnitId);
    const qualities = useQualities(false, dialogData.businessUnitId);
    const labFunctions = useLabFunctions();
    const offspecFunctions = useOffspecFunctions();

    const saveLineQualityData = (lineQualityData: LineQualityData) => {

        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/linequalitydata`;
            if (lineQualityData.lineQualityDataId != null) {
                url += `/${lineQualityData.lineQualityDataId}`
            }

            fetchDataAuthenticated(url, "POST", JSON.stringify(lineQualityData))
                .then(response => response.json())
                .then(data => {
                    if (data.errors !== null && data.errors !== undefined) {
                        reject(data);
                    }
                    resolve(data);
                })
                .catch(error => {
                    alert("There was a problem! " + error);
                });

        });

        return promise;
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleInputChange(e, dialogData, setDialogData, fieldErrors, setFieldErrors);
    }

    const onChangeFunction = (field: string, value: any, valid : boolean, validationMessage: string) => {
        setDialogData({
            ...dialogData,
            [field]: value
        });

        if (valid) {
            setFieldErrors({
                ...fieldErrors,
                [field]: null
            });
        }
        else {
            setFieldErrors({
                ...fieldErrors,
                [field]: validationMessage
            });
        }
    };

    const onChangeParams = (field: string, functionNameWithParams: string) => {
        setFieldErrors({
            ...fieldErrors,
            [field]: null
        });

        setDialogData({
            ...dialogData,
            [field]: functionNameWithParams
        });
    }

    interface QualityFunctionSelectProps {
        controlId: string,
        title: string,
        name: any,
        functions: Function[]
    }

    const QualityFunctionSelect = ({ controlId, title, name, functions }: QualityFunctionSelectProps) => {
        type FieldKey = keyof typeof dialogData;
        let fieldName: FieldKey = name;

        return <FunctionSelect controlId={controlId} title={title} name={name} functions={functions} onChangeFunction={onChangeFunction} onChangeParams={onChangeParams} value={dialogData[fieldName]} error={fieldErrors[fieldName]} />
    }

    return (
        <CommonConfigDialog title={"Line Quality Data"} dialogData={dialogData} showDialog={showDialog} closeDialog={closeDialog} save={saveLineQualityData} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors} wideDialog={true}>
            <RequiredDialogSelect controlId="formLineQualityBusinessUnit" title="Business Unit" name="businessUnitId" value={dialogData.businessUnitId} error={fieldErrors.businessUnitId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {businessUnits.map((bu) => <option value={bu.businessUnitId} key={bu.businessUnitId}>{bu.name}</option>)}
            </RequiredDialogSelect>
            <RequiredDialogSelect controlId="formLineQualityDataLine" title="Line" name="lineId" value={dialogData.lineId} error={fieldErrors.lineId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {lines.map((l : Line) => <option value={l.lineId} key={l.lineId}>{l.name}</option>)}
            </RequiredDialogSelect>
            <RequiredDialogSelect controlId="formLineQualityDataQuality" title="Quality" name="qualityId" value={dialogData.qualityId} error={fieldErrors.qualityId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {qualities.map((q : Quality) => <option value={q.qualityId} key={q.qualityId}>{q.name}</option>)}
            </RequiredDialogSelect>
            <DialogInput controlId="formLineQualityDefaultValue" title="Default Value To Use" name="defaultValueToUse" value={dialogData.defaultValueToUse} error={fieldErrors.defaultValueToUse} onChange={onChange}/>
            <QualityFunctionSelect controlId="formLineQualityLotLabCalc" title="Lot Lab Calc Func" name="lotLabCalculateFunction" functions={labFunctions} />
            <QualityFunctionSelect controlId="formLineQualityLotOffspec" title="Lot Offspec Func" name="lotIsOffspecFunction" functions={offspecFunctions} />
            <DialogCheckbox disabled={dialogData.lineQualityDataId === null} controlId="formLineQualityIsDeleted" title="Deleted?" name="isDeleted" checked={dialogData.isDeleted} error={fieldErrors.isDeleted} onChange={onChange}/>
        </CommonConfigDialog>
    );
}

export default LineQualityDataEditDialog;